import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth, useIsAdmin, useVeEquipa } from '@/features/auth/AuthContext';
import { useSettings } from '@/features/app/hooks';
import { initials } from '@/lib/format';
import { supportedLanguages } from '@/i18n';
import { AppLayout } from '@/components/AppLayout';
import { CodigoSheet } from '@/components/sheets/CodigoSheet';
import { Icon } from '@/components/ui/Icon';
import { useToast } from '@/components/ui/Toast';

const APP_VERSION = '1.0';

export function PerfilPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { user, logout } = useAuth();
  const veEquipa = useVeEquipa();
  const isAdmin = useIsAdmin();
  const settings = useSettings();
  const [codigoOpen, setCodigoOpen] = useState(false);

  if (!user) return null;

  const campos = [
    { label: t('perfil.utilizador'), valor: user.username },
    { label: t('perfil.numero'), valor: user.numeroMecanografico ?? '—' },
    { label: t('perfil.categoria'), valor: user.categoria ?? '—' },
    { label: t('perfil.telemovel'), valor: user.phoneNumber ?? '—' },
    { label: t('perfil.email'), valor: user.email ?? '—' },
    { label: t('perfil.desde'), valor: user.naEmpresaDesde ?? '—' },
    { label: t('perfil.permissoes'), valor: veEquipa ? t('perfil.veTudo') : t('perfil.soSeus') },
  ];

  return (
    <AppLayout title={t('header.perfil')} subtitle={user.displayName}>
      <div className="flex flex-col gap-4 md:grid md:grid-cols-2 md:items-start md:gap-x-6 md:gap-y-4">
        <section className="card flex items-center gap-3.5 p-4 md:col-span-2">
          <span className="grid h-[52px] w-[52px] shrink-0 place-items-center rounded-full bg-conduril text-[17px] font-semibold text-white">
            {initials(user.displayName)}
          </span>
          <div className="min-w-0">
            <div className="truncate text-[18px] font-semibold leading-tight text-conduril-ink">
              {user.displayName}
            </div>
            <div className="num mt-1.5 text-[11px] uppercase tracking-wide text-conduril-muted">
              {user.categoria ?? user.role}
            </div>
          </div>
        </section>

        <div className="md:row-span-2 md:flex md:flex-col md:gap-2">
        <div className="label-eyebrow">{t('perfil.dados')}</div>
        <ul className="card divide-y divide-[#f0f1f6] overflow-hidden">
          {campos.map((campo) => (
            <li key={campo.label} className="flex items-center justify-between gap-3 px-4 py-3.5">
              <span className="text-[12px] text-conduril-muted">{campo.label}</span>
              <span className="truncate text-right text-[13px] font-medium text-conduril-ink">
                {campo.valor}
              </span>
            </li>
          ))}
        </ul>
        </div>

        <div className="flex flex-col gap-4">
        {isAdmin && (
          <>
            <div className="label-eyebrow">{t('perfil.gestao')}</div>
            <RowLink
              icon="group"
              title={t('perfil.utilizadores')}
              sub={t('perfil.utilizadoresSub')}
              onClick={() => navigate('/utilizadores')}
            />
            <RowLink
              icon="tune"
              title={t('perfil.definicoes')}
              sub={t('perfil.definicoesSub')}
              onClick={() => navigate('/definicoes')}
            />
          </>
        )}

        <div className="label-eyebrow">{t('perfil.seguranca')}</div>
        <RowLink
          icon="password"
          title={t('perfil.alterarCodigo')}
          sub={t('perfil.alterarCodigoSub', {
            quando: user.mustChangePassword ? t('perfil.nuncaAlterado') : '—',
          })}
          onClick={() => setCodigoOpen(true)}
        />

        <div className="card flex items-center justify-between gap-3 p-4">
          <span className="flex items-center gap-3">
            <Icon name="notifications" className="text-[20px] text-conduril" />
            <span className="flex flex-col gap-1">
              <span className="text-[13px] font-semibold text-conduril-ink">
                {t('perfil.avisoDiario', { hora: settings.data?.avisoDiarioHora ?? '18:00' })}
              </span>
              <span className="text-[11px] text-conduril-muted">{t('perfil.avisoDiarioSub')}</span>
            </span>
          </span>
          <span
            className={`num shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${
              settings.data?.avisoDiarioAtivo
                ? 'bg-ok-bg text-ok-text'
                : 'bg-[#f0f1f6] text-conduril-muted'
            }`}
          >
            {settings.data?.avisoDiarioAtivo ? t('common.yes') : t('common.no')}
          </span>
        </div>

        <div className="label-eyebrow">{t('perfil.idioma')}</div>
        <div className="flex gap-2">
          {supportedLanguages.map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => void i18n.changeLanguage(lang)}
              className={`flex-1 rounded-xl border px-3 py-2.5 text-[12px] font-semibold uppercase transition ${
                i18n.resolvedLanguage === lang
                  ? 'border-conduril bg-conduril-tint text-conduril'
                  : 'border-conduril-border bg-white text-conduril-muted'
              }`}
            >
              {lang}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => void logout()}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-2xl border border-danger-border bg-white px-4 py-3.5 text-[13px] font-semibold text-danger-text"
        >
          <Icon name="logout" className="text-[18px]" /> {t('perfil.logout')}
        </button>

        </div>

        <div className="num mt-2 text-center text-[10px] leading-relaxed text-[#a0a5b2] md:col-span-2">
          {t('app.version', { version: APP_VERSION })}
        </div>
      </div>

      <CodigoSheet
        open={codigoOpen}
        onClose={() => setCodigoOpen(false)}
        onDone={() => {
          setCodigoOpen(false);
          toast.show(t('sheet.codigoAlterado'));
        }}
      />
    </AppLayout>
  );
}

function RowLink({
  icon,
  title,
  sub,
  onClick,
}: {
  icon: string;
  title: string;
  sub: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between gap-3 rounded-2xl border border-conduril-border bg-white p-4 text-left shadow-card transition hover:border-[#c3cae0]"
    >
      <span className="flex min-w-0 items-center gap-3">
        <Icon name={icon} className="shrink-0 text-[20px] text-conduril" />
        <span className="flex min-w-0 flex-col gap-1">
          <span className="truncate text-[13px] font-semibold text-conduril-ink">{title}</span>
          <span className="truncate text-[11px] text-conduril-muted">{sub}</span>
        </span>
      </span>
      <Icon name="chevron_right" className="shrink-0 text-[18px] text-[#b6bbc8]" />
    </button>
  );
}
